import { UnknownNetworkError } from './../commonModels/apiResponse';
import { FacebookAccessToken, FacebookEmail } from './../../domain/facebook';
import { AccessToken, RefreshToken } from './../../domain/token';
import FacebookHandler from '../../gateways/social/facebookHandler';
import facebookApi from '../../gateways/api/facebookApi';
import { UserId } from '../../domain/user';
import { Either, Right } from '../../utils/either';
import { Option, None } from '../../utils/option';
import TokenStorage from '../commonModels/tokenStorage';
import tracker from '../../gateways/tracker/tracker';
import i18n from '../../R/string/i18n';
import { WA_SERVICE } from '../../domain/waService';

export interface FacebookRegisterApiSuccess {
  sessionType: 'signup' | 'signin';
  accessToken: AccessToken;
  refreshToken: RefreshToken;
  userId: UserId;
  email: Option<FacebookEmail>;
  facebookAccessToken: FacebookAccessToken;
}

interface RegisterSuccess {
  sessionType: 'signup' | 'signin' | 'waitCheckingEmail';
  email: Option<FacebookEmail>;
  facebookAccessToken: FacebookAccessToken;
}

export class FacebookRegisterError implements Error {
  name = 'FacebookRegisterError';
  constructor(readonly message: string) {}
}

export class FacebookNotGrantedAccessError implements Error {
  name = 'FacebookNotGrantedAccessError';
  message = i18n.socialLoginUserDidntGrantedError();
}

export type FacebookRegisterErrors =
  | FacebookRegisterError
  | UnknownNetworkError
  | FacebookNotGrantedAccessError;

const registerWith =
  (tokenStorage: TokenStorage) =>
  async (
    waService: WA_SERVICE
  ): Promise<Either<FacebookRegisterErrors, RegisterSuccess>> => {
    const tokenOrError = await FacebookHandler.instance.login();

    const facebookResultOrError = await tokenOrError.flatMapAsync(
      async (token) => facebookApi.register(token, waService)
    );

    return facebookResultOrError.flatMapAsync(
      async ({
        sessionType,
        refreshToken,
        accessToken,
        userId,
        facebookAccessToken,
        email
      }) => {
        tokenStorage.setAccessToken(accessToken);
        tokenStorage.setRefreshToken(refreshToken);

        if (sessionType === 'signup') {
          tracker.startTracking(userId);
          tracker.trackSocialAccountRegistration(userId);

          return (await FacebookHandler.instance.me())
            .leftMap<FacebookRegisterErrors>(() => new UnknownNetworkError())
            .map<RegisterSuccess>((fbEmail) =>
              fbEmail.notEquals(email)
                ? {
                    sessionType: 'waitCheckingEmail',
                    email: None,
                    facebookAccessToken
                  }
                : {
                    sessionType: 'signup',
                    email: fbEmail,
                    facebookAccessToken
                  }
            );
        }

        tracker.resumeTracking(userId);
        return new Right<FacebookRegisterErrors, RegisterSuccess>({
          sessionType: 'signin',
          email: None,
          facebookAccessToken
        });
      }
    );
  };

export interface FacebookConfirmResult {
  type:
    | 'emailSameAsFacebookMail'
    | 'emailDifferentFromFacebookMail'
    | 'checkEmailBox';
}

export class FacebookConfirmGeneralError implements Error {
  name = 'FacebookConfirmEmailAlreadyRegistered';
  constructor(readonly message: string) {}
}

export class FacebookConfirmEmailAlreadyRegisteredError implements Error {
  name = 'FacebookConfirmEmailAlreadyRegistered';
  message = i18n.socialLoginEmailAlreadyRegistered();
}

export type FacebookConfirmErrorTypes =
  | FacebookConfirmEmailAlreadyRegisteredError
  | UnknownNetworkError
  | FacebookConfirmGeneralError;

const confirm = async (
  email: FacebookEmail,
  facebookAccessToken: FacebookAccessToken,
  redirectUrl: string
) => {
  const res = await facebookApi.confirm(email, facebookAccessToken);

  return res.flatMapAsync((response) => {
    if (response.type === 'emailSameAsFacebookMail') {
      return Promise.resolve(
        new Right<FacebookConfirmErrorTypes, FacebookConfirmResult>(response)
      );
    }
    return facebookApi.sendConfirmationMail(email, redirectUrl);
  });
};

export default {
  registerWith,
  confirm
};
